// 알림·자료 게시판 (공지사항 / 법령·정책·서식) 공통 모양 (브라우저에서도 씀)

export type BoardKey = 'notice' | 'policy';

export const BOARDS: Record<BoardKey, { label: string; short: string; path: string; admin: string; desc: string }> = {
  notice: {
    label: '공지사항',
    short: '공지사항',
    path: '/notice',
    admin: '/admin/notices',
    desc: '넘선생 업무 안내와 새 소식을 알려 드립니다.',
  },
  policy: {
    label: '법령·정책·서식',
    short: '법령·정책·서식',
    path: '/policy',
    admin: '/admin/policies',
    desc: '화물운송 관련 법령·정책 변경 내용과 신고 서식, 계약서 양식을 받아 가실 수 있습니다.',
  },
};

export const BOARD_SECTION_LABEL = '알림·자료';

export const isBoardKey = (v: unknown): v is BoardKey => v === 'notice' || v === 'policy';

export type BoardCategory = { id: string; board: BoardKey; name: string; sort_order: number };

export type BoardFile = {
  id: string;
  post_id: string;
  path: string;
  name: string;
  size: number;
  mime: string | null;
  sort_order: number;
  download_count: number;
};

export type BoardPost = {
  id: string;
  no: number;
  board: BoardKey;
  category_id: string | null;
  title: string;
  summary: string | null;
  body_html: string;
  effective_date: string | null;
  source_name: string | null;
  source_url: string | null;
  is_pinned: boolean;
  pinned_at: string | null;
  show_on_home: boolean;
  is_published: boolean;
  publish_at: string | null;
  view_count: number;
  created_at: string;
  updated_at: string;
};

export const POST_SELECT =
  'id, no, board, category_id, title, summary, body_html, effective_date, source_name, source_url, is_pinned, pinned_at, show_on_home, is_published, publish_at, view_count, created_at, updated_at';
export const POST_LIST_SELECT =
  'id, no, board, category_id, title, summary, effective_date, source_name, is_pinned, pinned_at, show_on_home, is_published, publish_at, view_count, created_at, updated_at';
export const FILE_SELECT = 'id, post_id, path, name, size, mime, sort_order, download_count';

export const BOARD_PAGE_SIZE = 20;
export const MAX_FILES = 20;
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

// 올릴 수 있는 파일 (확장자 기준)
export const FILE_EXTS = ['hwp', 'hwpx', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
export const FILE_ACCEPT = FILE_EXTS.map((e) => `.${e}`).join(',');

export const fileExt = (name: string) => (name.includes('.') ? name.split('.').pop()!.toLowerCase() : '');

export function fileSizeLabel(bytes: number) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

// 파일 종류 표시 (글자 + 색)
export function fileBadge(name: string) {
  const ext = fileExt(name);
  if (ext === 'hwp' || ext === 'hwpx') return { label: 'HWP', cls: 'bg-sky-100 text-sky-700' };
  if (ext === 'pdf') return { label: 'PDF', cls: 'bg-rose-100 text-rose-700' };
  if (ext === 'doc' || ext === 'docx') return { label: 'DOC', cls: 'bg-blue-100 text-blue-700' };
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return { label: 'XLS', cls: 'bg-emerald-100 text-emerald-700' };
  if (ext === 'ppt' || ext === 'pptx') return { label: 'PPT', cls: 'bg-orange-100 text-orange-700' };
  if (ext === 'zip') return { label: 'ZIP', cls: 'bg-amber-100 text-amber-700' };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return { label: 'IMG', cls: 'bg-violet-100 text-violet-700' };
  return { label: ext.toUpperCase().slice(0, 4) || 'FILE', cls: 'bg-secondary text-text-secondary' };
}

// 공개 상태: 공개 / 숨김 / 예약
export function postVisibility(p: Pick<BoardPost, 'is_published' | 'publish_at'>, now = Date.now()) {
  if (!p.is_published) return 'hidden' as const;
  if (p.publish_at && new Date(p.publish_at).getTime() > now) return 'scheduled' as const;
  return 'public' as const;
}

// 글 날짜 = 예약 공개 시각이 있으면 그 시각, 아니면 쓴 날
export const postDate = (p: Pick<BoardPost, 'publish_at' | 'created_at'>) => p.publish_at ?? p.created_at;

export const isNewPost = (p: Pick<BoardPost, 'publish_at' | 'created_at'>, now = Date.now()) => now - new Date(postDate(p)).getTime() < 7 * 86400000;

export const fmtYmd = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\s/g, '').replace(/\.$/, '') : '';

export const fmtDateDots = (d: string | null) => (d ? d.replace(/-/g, '.') : '');

export const boardPostPath = (board: BoardKey, no: number) => `${BOARDS[board].path}/${no}`;
